/**
 * 將任意來源的 id 轉成可比對的 24 字元小寫 hex（Mongo ObjectId）。
 * 相容：字串、數字、{ $oid }、具 toHexString 的 BSON、Mongoose 序列化物件。
 */
export function toCanonicalStylistHex (raw) {
  if (raw == null || raw === '') return ''
  if (typeof raw === 'string' || typeof raw === 'number') {
    const s = String(raw).trim().toLowerCase()
    return /^[a-f0-9]{24}$/.test(s) ? s : ''
  }
  if (typeof raw === 'object') {
    if (raw.$oid != null) return toCanonicalStylistHex(raw.$oid)
    if (typeof raw.toHexString === 'function') {
      const h = String(raw.toHexString()).trim().toLowerCase()
      return /^[a-f0-9]{24}$/.test(h) ? h : ''
    }
    const s = String(raw).trim().toLowerCase()
    if (/^[a-f0-9]{24}$/.test(s)) return s
  }
  return ''
}

/** 從設計師列表 API 的一筆資料取出可比對的 id */
export function canonicalStylistIdFromRow (row) {
  if (!row || typeof row !== 'object') return ''
  return toCanonicalStylistHex(row._id ?? row.id ?? row.userId)
}

/**
 * 將服務上的 stylists 正規化為「設計師 _id 字串陣列」，供 Ant Design Select mode=multiple 使用。
 * 歷史資料可能混有：ObjectId 字串、物件 { _id }、或誤存顯示名稱（與 id 並存造成重複 tag）。
 */
export function normalizeProductStylistIds (rawStylists, stylistList = []) {
  const list = Array.isArray(rawStylists) ? rawStylists : []
  const nameToId = new Map()
  for (const s of stylistList) {
    const id = canonicalStylistIdFromRow(s)
    if (!id) continue
    const name = String(s?.name || '').trim()
    if (name) nameToId.set(name, id)
  }

  const seen = new Set()
  const out = []

  for (const item of list) {
    if (item == null) continue
    let id = null
    if (typeof item === 'string' || typeof item === 'number') {
      const str = String(item).trim()
      const hex = toCanonicalStylistHex(str)
      if (hex) id = hex
      else {
        const mapped = nameToId.get(str)
        if (mapped) id = mapped
      }
    } else if (typeof item === 'object') {
      const hex = toCanonicalStylistHex(item._id ?? item.id ?? item)
      if (hex) id = hex
    }
    if (id && /^[a-f0-9]{24}$/.test(id) && !seen.has(id)) {
      seen.add(id)
      out.push(id)
    }
  }
  return out
}

/**
 * 多選標籤顯示：優先顯示設計師姓名，避免 value 與 API 回傳的 _id 型別不一致時露裸 id。
 */
export function resolveStylistTagLabel (value, stylistList = []) {
  if (value == null || value === '') return ''
  if (typeof value === 'object' && value != null && value._id != null) {
    return resolveStylistTagLabel(value._id, stylistList)
  }
  const hex = toCanonicalStylistHex(value)
  if (hex) {
    const byId = stylistList.find((s) => canonicalStylistIdFromRow(s) === hex)
    if (byId) {
      const label = String(
        byId.name || byId.username || ''
      ).trim()
      if (label) return label
    }
    return hex
  }
  const v = String(value).trim()
  const byName = stylistList.find(
    (s) => String(s.name || '').trim() === v
  )
  if (byName) {
    const label = String(byName.name || byName.username || '').trim()
    if (label) return label
  }
  return v
}

/** 給 Select `options` 用：{ label, value } */
export function stylistsToSelectOptions (stylistList = []) {
  return stylistList
    .map((stylist) => {
      const value = canonicalStylistIdFromRow(stylist)
      if (!value) return null
      const label =
        String(stylist.name || stylist.username || '').trim() || value
      return { label, value }
    })
    .filter(Boolean)
}
